import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApi, useTenantContext } from '../lib/api';

type HealthStatus = 'healthy' | 'degraded' | 'critical';
type AlertSeverity = 'info' | 'warn' | 'critical';

type OperationalCaseEvent = {
  event_id: string;
  case_id: string;
  occurred_at: string;
  event_type: string;
  severity: string;
  category: string;
  summary: string;
};

type OperationalAuditEvent = {
  audit_id: string;
  case_id: string | null;
  action: string;
  actor_type: string;
  actor_id: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
  reason: string | null;
  created_at: string;
};

type AiRun = {
  ai_run_id: string;
  case_id: string | null;
  occurred_at: string;
  provider: string | null;
  final_provider: string | null;
  model_id: string | null;
  prompt_version: string | null;
  latency_ms: number | null;
  status: 'succeeded' | 'failed' | 'skipped';
  fallback_used: boolean;
  decision: {
    action: string | null;
    reason: string | null;
    intent: string | null;
    confidence: number | null;
  };
};

type TenantHealthComponent = {
  component: string;
  status: HealthStatus;
  title: string;
  summary: string;
  signals: Record<string, number | string | null>;
};

type OperationalAlert = {
  alert_id: string;
  severity: AlertSeverity;
  component: string;
  title: string;
  description: string;
  signal: string;
  count: number;
  created_at: string;
  runbook: string[];
  related_case_id?: string | null;
};

type OperationsResponse = {
  tenant_id: string;
  from: string;
  to: string;
  generated_at: string;
  summary: {
    case_events: number;
    audit_events: number;
    ai_runs: number;
    ai_failures: number;
    ai_fallbacks: number;
    average_ai_latency_ms: number | null;
  };
  tenant_health: {
    status: HealthStatus;
    score: number;
    components: TenantHealthComponent[];
  };
  alerts: OperationalAlert[];
  case_events: OperationalCaseEvent[];
  audit_events: OperationalAuditEvent[];
  ai_runs: AiRun[];
  errors: Record<string, string | null>;
};

function defaultFrom() {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - 6);
  return date.toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR').format(value);
}

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString('pt-BR') : '-';
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatMs(value: number | null) {
  if (value === null) return '-';
  if (value < 1000) return `${value}ms`;
  return `${(value / 1000).toFixed(1)}s`;
}

function formatStatus(status: HealthStatus) {
  if (status === 'critical') return 'Critico';
  if (status === 'degraded') return 'Degradado';
  return 'Saudavel';
}

function formatSeverity(severity: AlertSeverity) {
  if (severity === 'critical') return 'Critico';
  if (severity === 'warn') return 'Aviso';
  return 'Info';
}

function toneFromStatus(status: HealthStatus): 'warn' | 'alert' | undefined {
  if (status === 'critical') return 'alert';
  if (status === 'degraded') return 'warn';
  return undefined;
}

function formatSignals(signals: Record<string, number | string | null>) {
  return Object.entries(signals)
    .map(([key, value]) => `${key}: ${value ?? '-'}`)
    .join(' | ');
}

function MetricCard(props: { label: string; value: string; tone?: 'warn' | 'alert' | undefined }) {
  return (
    <div className={['metric-card', props.tone ? `metric-card-${props.tone}` : ''].join(' ')}>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
    </div>
  );
}

export default function OperationsPage() {
  const { tenant_id, hasScope } = useTenantContext();
  const { request } = useApi();
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(today());
  const [data, setData] = useState<OperationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!tenant_id || !hasScope('METRICS_VIEW')) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    request<OperationsResponse>(
      `/tenants/${encodeURIComponent(tenant_id)}/operations?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=50`,
    )
      .then((out) => {
        if (!cancelled) setData(out);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err instanceof Error ? err.message : err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [from, hasScope, request, tenant_id, to]);

  useEffect(() => {
    return load();
  }, [load]);

  if (!tenant_id) return <main className="page">Usuario sem tenant configurado.</main>;
  if (!hasScope('METRICS_VIEW')) return <main className="page">Sem permissao operacional.</main>;

  const aiFailureRate = data?.summary.ai_runs
    ? data.summary.ai_failures / data.summary.ai_runs
    : null;
  const criticalAlerts = data?.alerts.filter((alert) => alert.severity === 'critical').length ?? 0;

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Operacao</h1>
          <p>Tenant {tenant_id}</p>
        </div>
        <div className="header-actions">
          <label className="compact-filter">
            De
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label className="compact-filter">
            Ate
            <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
          <button type="button" className="secondary" onClick={() => load()} disabled={loading}>
            Atualizar
          </button>
          <Link className="nav-link" to="/metrics">
            Metricas
          </Link>
          <Link className="nav-link" to="/cost-center">
            Custos
          </Link>
          <Link className="nav-link" to="/">
            Inbox
          </Link>
        </div>
      </header>

      {error ? <p className="error">{error}</p> : null}
      {loading && !data ? <p className="hint">Carregando operacao...</p> : null}

      {data ? (
        <>
          <section className="metric-grid metric-grid-technical">
            <MetricCard
              label="Saude geral"
              value={formatStatus(data.tenant_health.status)}
              tone={toneFromStatus(data.tenant_health.status)}
            />
            <MetricCard
              label="Score operacional"
              value={`${data.tenant_health.score}/100`}
              tone={toneFromStatus(data.tenant_health.status)}
            />
            <MetricCard
              label="Alertas criticos"
              value={formatNumber(criticalAlerts)}
              tone={criticalAlerts > 0 ? 'alert' : undefined}
            />
            <MetricCard
              label="Alertas ativos"
              value={formatNumber(data.alerts.length)}
              tone={data.alerts.length > 0 ? 'warn' : undefined}
            />
          </section>

          <section className="panel metric-table metric-detail-table metric-table-wide">
            <h2>Tenant Health</h2>
            <table>
              <thead>
                <tr>
                  <th>Componente</th>
                  <th>Status</th>
                  <th>Resumo</th>
                  <th>Sinais</th>
                </tr>
              </thead>
              <tbody>
                {data.tenant_health.components.map((component) => (
                  <tr key={component.component}>
                    <td>{component.title}</td>
                    <td className={`status-cell status-cell-${component.status}`}>
                      {formatStatus(component.status)}
                    </td>
                    <td>{component.summary}</td>
                    <td>{formatSignals(component.signals)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="panel metric-table metric-detail-table metric-table-wide">
            <h2>Alert Center</h2>
            {data.alerts.length ? (
              <table>
                <thead>
                  <tr>
                    <th>Severidade</th>
                    <th>Componente</th>
                    <th>Alerta</th>
                    <th>Runbook</th>
                    <th>Caso</th>
                  </tr>
                </thead>
                <tbody>
                  {data.alerts.map((alert) => (
                    <tr key={alert.alert_id}>
                      <td className={`status-cell status-cell-${alert.severity}`}>
                        {formatSeverity(alert.severity)}
                      </td>
                      <td>{alert.component}</td>
                      <td>
                        <strong>{alert.title}</strong>
                        <span className="table-subtext">{alert.description}</span>
                      </td>
                      <td>
                        <ol className="runbook-list">
                          {alert.runbook.map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ol>
                      </td>
                      <td>
                        {alert.related_case_id ? (
                          <Link to={`/cases/${alert.related_case_id}`}>
                            {alert.related_case_id}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="hint">Sem alertas operacionais no periodo.</p>
            )}
          </section>

          <section className="metric-grid metric-grid-technical">
            <MetricCard label="Eventos de case" value={formatNumber(data.summary.case_events)} />
            <MetricCard label="Auditorias" value={formatNumber(data.summary.audit_events)} />
            <MetricCard label="AI Runs" value={formatNumber(data.summary.ai_runs)} />
            <MetricCard
              label="Falhas IA"
              value={formatNumber(data.summary.ai_failures)}
              tone={data.summary.ai_failures > 0 ? 'alert' : undefined}
            />
            <MetricCard
              label="Fallback IA"
              value={formatNumber(data.summary.ai_fallbacks)}
              tone={data.summary.ai_fallbacks > 0 ? 'warn' : undefined}
            />
            <MetricCard label="Latencia IA" value={formatMs(data.summary.average_ai_latency_ms)} />
            <MetricCard
              label="Taxa falha IA"
              value={formatPercent(aiFailureRate)}
              tone={aiFailureRate && aiFailureRate > 0 ? 'warn' : undefined}
            />
          </section>

          {Object.values(data.errors).some(Boolean) ? (
            <section className="panel metric-table metric-detail-table metric-table-wide">
              <h2>Avisos de coleta</h2>
              <table>
                <tbody>
                  {Object.entries(data.errors)
                    .filter(([, value]) => Boolean(value))
                    .map(([key, value]) => (
                      <tr key={key}>
                        <th>{key}</th>
                        <td>{value}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </section>
          ) : null}

          <section className="panel metric-table metric-detail-table metric-table-wide">
            <h2>AI Runs recentes</h2>
            {data.ai_runs.length ? (
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Caso</th>
                    <th>Status</th>
                    <th>Provider</th>
                    <th>Intencao</th>
                    <th>Confianca</th>
                    <th>Latencia</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ai_runs.map((run) => (
                    <tr key={run.ai_run_id}>
                      <td>{formatDate(run.occurred_at)}</td>
                      <td>
                        {run.case_id ? (
                          <Link to={`/cases/${run.case_id}`}>{run.case_id}</Link>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {run.status}
                        {run.fallback_used ? ' / fallback' : ''}
                      </td>
                      <td>{run.final_provider ?? run.provider ?? '-'}</td>
                      <td>{run.decision.intent ?? '-'}</td>
                      <td>{formatPercent(run.decision.confidence)}</td>
                      <td>{formatMs(run.latency_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="hint">Sem AI Runs no periodo.</p>
            )}
          </section>

          <section className="panel metric-table metric-detail-table metric-table-wide">
            <h2>Audit Trail</h2>
            {data.audit_events.length ? (
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Acao</th>
                    <th>Ator</th>
                    <th>Recurso</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.audit_events.map((event) => (
                    <tr key={event.audit_id}>
                      <td>{formatDate(event.created_at)}</td>
                      <td>{event.action}</td>
                      <td>{event.actor_id ?? event.actor_type}</td>
                      <td>
                        {event.resource_type ?? 'case'}:{event.resource_id ?? event.case_id ?? '-'}
                      </td>
                      <td>{event.reason ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="hint">Sem auditoria no periodo.</p>
            )}
          </section>

          <section className="panel metric-table metric-detail-table metric-table-wide">
            <h2>Timeline operacional</h2>
            {data.case_events.length ? (
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Caso</th>
                    <th>Evento</th>
                    <th>Categoria</th>
                    <th>Resumo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.case_events.map((event) => (
                    <tr key={event.event_id}>
                      <td>{formatDate(event.occurred_at)}</td>
                      <td>
                        <Link to={`/cases/${event.case_id}`}>{event.case_id}</Link>
                      </td>
                      <td>{event.event_type}</td>
                      <td>{event.category}</td>
                      <td>{event.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="hint">Sem eventos de case no periodo.</p>
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}
